import React, { useState, useEffect, useRef, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../services/firebase';
import { collection, addDoc, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ArrowLeft, Save, Upload, X } from 'lucide-react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { ThemeContext } from '../App';

interface AdminProjectEditorProps {
    mode: 'create' | 'edit';
}

const initialForm = {
    title: '',
    description: '',
    content: '',
    technologies: '',
    github: '',
    demo: '',
    image: '',
    category: 'core' as 'core' | 'side',
    order: 0,
};

// Custom CKEditor upload adapter for Cloudinary with base64 fallback
function CustomUploadAdapterPlugin(editor: any) {
    editor.plugins.get('FileRepository').createUploadAdapter = (loader: any) => {
        return {
            upload: async () => {
                const file = await loader.file;
                try {
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('upload_preset', 'ml_default');
                    const response = await fetch('https://api.cloudinary.com/v1_1/demo/image/upload', {
                        method: 'POST',
                        body: formData,
                    });
                    if (response.ok) {
                        const data = await response.json();
                        return { default: data.secure_url };
                    } else {
                        throw new Error('Cloudinary upload failed');
                    }
                } catch (error) {
                    console.log('Cloudinary failed, using base64 fallback');
                    return new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onload = (e) => {
                            const base64String = e.target?.result as string;
                            resolve({ default: base64String });
                        };
                        reader.onerror = () => reject(new Error('Upload failed'));
                        reader.readAsDataURL(file);
                    });
                }
            },
            abort: () => { },
        };
    };
}

const AdminProjectEditor: React.FC<AdminProjectEditorProps> = ({ mode }) => {
    const [form, setForm] = useState(initialForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [imageUploading, setImageUploading] = useState(false);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const { theme } = useContext(ThemeContext);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // CKEditor dark mode styles
    useEffect(() => {
        const styleId = 'ckeditor-dark-mode-style-projects';
        let styleTag = document.getElementById(styleId);
        if (theme === 'dark') {
            if (!styleTag) {
                styleTag = document.createElement('style');
                styleTag.id = styleId;
                styleTag.innerHTML = `
                    .ck.ck-editor__main > .ck-editor__editable { background: hsl(0 0% 7%) !important; color: #fff !important; }
                    .ck.ck-toolbar { background: hsl(0 0% 5%) !important; color: #fff !important; }
                    .ck.ck-toolbar .ck-button .ck-button__label, .ck.ck-toolbar .ck-button .ck-icon { color: #fff !important; }
                    .ck.ck-dropdown .ck-dropdown__panel { background: hsl(0 0% 7%) !important; color: #fff !important; }
                    .ck.ck-list__item .ck-button__label { color: #fff !important; }
                    .ck.ck-editor__editable { caret-color: #fff !important; }
                `;
                document.head.appendChild(styleTag);
            }
        } else {
            if (styleTag) styleTag.remove();
        }
        return () => {
            const tag = document.getElementById(styleId);
            if (tag) tag.remove();
        };
    }, [theme]);

    useEffect(() => {
        const fetchProject = async () => {
            if (mode === 'edit' && id) {
                try {
                    const docRef = doc(db, 'projects', id);
                    const docSnap = await getDoc(docRef);
                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setForm({
                            title: data.title || '',
                            description: data.description || '',
                            content: data.content || '',
                            technologies: (data.technologies || []).join(', '),
                            github: data.github || '',
                            demo: data.demo || '',
                            image: data.image || '',
                            category: data.category || 'core',
                            order: data.order || 0,
                        });
                    }
                } catch (err) {
                    setError('Failed to load project.');
                }
            }
        };
        fetchProject();
    }, [mode, id]);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file.');
            return;
        }
        if (file.size > 5 * 1024 * 1024) {
            setError('Image size should be less than 5MB.');
            return;
        }

        setImageUploading(true);
        setError('');

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('upload_preset', 'ml_default');
            const response = await fetch('https://api.cloudinary.com/v1_1/demo/image/upload', {
                method: 'POST',
                body: formData,
            });
            if (response.ok) {
                const data = await response.json();
                setForm(f => ({ ...f, image: data.secure_url }));
                setSuccess('Image uploaded successfully!');
            } else {
                throw new Error('Cloudinary upload failed');
            }
        } catch (err) {
            // Fallback to base64
            try {
                const reader = new FileReader();
                reader.onload = (ev) => {
                    const base64String = ev.target?.result as string;
                    setForm(f => ({ ...f, image: base64String }));
                    setSuccess('Image uploaded (base64).');
                };
                reader.onerror = () => setError('Failed to read image file.');
                reader.readAsDataURL(file);
            } catch (fallbackErr) {
                setError('Failed to upload image.');
            }
        } finally {
            setImageUploading(false);
        }
    };

    const removeImage = () => {
        setForm(f => ({ ...f, image: '' }));
    };

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const projectData = {
                title: form.title,
                description: form.description,
                content: form.content || '',
                technologies: form.technologies
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean),
                github: form.github || null,
                demo: form.demo || null,
                image: form.image || null,
                category: form.category,
                order: Number(form.order),
            };

            if (mode === 'create') {
                await addDoc(collection(db, 'projects'), projectData);
            } else if (id) {
                await updateDoc(doc(db, 'projects', id), projectData);
            }

            navigate('/admin/projects');
        } catch (err) {
            setError('Failed to save project.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="mx-auto max-w-3xl md:border-x">
            <div className="px-4 py-12">
                <button
                    onClick={() => navigate('/admin/projects')}
                    className="mb-6 text-sm text-muted-foreground hover:text-foreground flex items-center gap-2 transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Back to Projects
                </button>

                <h1 className="text-2xl font-bold text-foreground mb-8">
                    {mode === 'create' ? 'New Project' : 'Edit Project'}
                </h1>

                {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                {success && <p className="text-green-600 text-sm mb-4">{success}</p>}

                <form onSubmit={handleSave} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Title</label>
                        <input
                            type="text"
                            name="title"
                            value={form.title}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">Description</label>
                        <textarea
                            name="description"
                            value={form.description}
                            onChange={handleChange}
                            required
                            rows={3}
                            className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>

                    {/* Featured Image Upload */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Featured Image
                        </label>
                        <div className="space-y-3">
                            {form.image && (
                                <div className="relative">
                                    <img
                                        src={form.image}
                                        alt="Featured"
                                        className="w-full h-48 object-cover rounded-lg border border-border"
                                    />
                                    <button
                                        type="button"
                                        onClick={removeImage}
                                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                                    >
                                        <X className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            <div className="flex items-center gap-3">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={imageUploading}
                                    className="flex items-center gap-2 px-4 py-2 text-sm border border-border rounded-md text-foreground hover:bg-accent transition-colors disabled:opacity-50"
                                >
                                    {imageUploading ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-foreground" />
                                            Uploading...
                                        </>
                                    ) : (
                                        <>
                                            <Upload className="w-4 h-4" />
                                            Upload Image
                                        </>
                                    )}
                                </button>
                                <span className="text-xs text-muted-foreground">Max 5MB</span>
                            </div>
                        </div>
                    </div>

                    {/* Rich Text Content */}
                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Content (detailed write-up)
                        </label>
                        <div className="rounded-md border border-border overflow-hidden min-h-[300px]">
                            <CKEditor
                                editor={ClassicEditor as any}
                                data={form.content}
                                config={{
                                    toolbar: [
                                        'heading', '|', 'bold', 'italic', 'link', 'bulletedList', 'numberedList', 'blockQuote',
                                        'insertTable', 'imageUpload', 'mediaEmbed', 'undo', 'redo', 'codeBlock'
                                    ],
                                    table: { contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells'] },
                                    extraPlugins: [CustomUploadAdapterPlugin],
                                }}
                                onChange={(_event: any, editor: any) => {
                                    const data = editor.getData();
                                    setForm(f => ({ ...f, content: data }));
                                }}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                            Technologies (comma-separated)
                        </label>
                        <input
                            type="text"
                            name="technologies"
                            value={form.technologies}
                            onChange={handleChange}
                            placeholder="Python, TensorFlow, Docker"
                            className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">GitHub URL</label>
                            <input
                                type="url"
                                name="github"
                                value={form.github}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Demo URL</label>
                            <input
                                type="url"
                                name="demo"
                                value={form.demo}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
                            <select
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            >
                                <option value="core">Core (Built to Last)</option>
                                <option value="side">Side Project</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-foreground mb-2">
                                Display Order
                            </label>
                            <input
                                type="number"
                                name="order"
                                value={form.order}
                                onChange={handleChange}
                                min={0}
                                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-3 pt-4">
                        <button
                            type="submit"
                            disabled={saving || imageUploading}
                            className="flex items-center gap-2 px-6 py-2 text-sm font-medium bg-foreground text-background rounded-md hover:opacity-90 transition-opacity disabled:opacity-50"
                        >
                            <Save size={14} />
                            {saving ? 'Saving...' : mode === 'create' ? 'Create Project' : 'Save Changes'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/admin/projects')}
                            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminProjectEditor;
